import { Box, Button, Tooltip } from '@chakra-ui/react';
import React from 'react';

import type { SlotProps } from '../../interface/slotProps';
import { useCaseNotesActivityPanel } from '../CaseNotesActivityPanelContext';

const CASE_NOTES_LABEL = 'Case notes';

const asCount = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0
    ? Math.floor(value)
    : 0;

const NoteIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M7 3h8l4 4v14H7z" />
    <line x1="9" y1="9" x2="15" y2="9" />
    <line x1="9" y1="13" x2="15" y2="13" />
    <line x1="9" y1="17" x2="13" y2="17" />
  </svg>
);

// The single panel is shared across both bar actions via context, so this only signals intent --
// it does NOT render the panel itself (unlike HoldAction's self-owned modal).
export const CaseNotesAction: React.FC<SlotProps> = ({ value }) => {
  const { open } = useCaseNotesActivityPanel();
  const count = asCount(value);

  const label =
    count > 0 ? `${CASE_NOTES_LABEL} (${count})` : CASE_NOTES_LABEL;

  return (
    <Tooltip label={label} placement="bottom" openDelay={300}>
    <Button
      data-testid="case-notes-icon"
      aria-label={label}
      onClick={() => open('notes')}
      variant="unstyled"
      position="relative"
      flexShrink={0}
      display="grid"
      placeItems="center"
      w="26px"
      h="26px"
      minW="26px"
      p={0}
      borderRadius="7px"
      bg="#e3eef9"
      color="#1f4f7a"
      _hover={{ filter: 'brightness(0.94)' }}
      sx={{ '& svg': { display: 'block' } }}
    >
      <NoteIcon />
      {count > 0 && (
        <Box
          data-testid="case-notes-count"
          aria-hidden
          position="absolute"
          top="-5px"
          right="-5px"
          minW="15px"
          h="15px"
          px="3px"
          borderRadius="999px"
          bg="#8B1FC8"
          color="#ffffff"
          fontSize="9.5px"
          lineHeight="15px"
          fontWeight="bold"
          textAlign="center"
          boxShadow="0 0 0 2px #fff"
        >
          {count > 99 ? '99+' : count}
        </Box>
      )}
    </Button>
    </Tooltip>
  );
};
